/* eslint-disable no-underscore-dangle */
import {
  CategoryHotRankTabVO,
  getHotRankList,
  hotRankCategoryList,
  HotRankDetailModel,
  hotRankProductList,
  RankProductListModel,
} from "@/api/rank";
import { useCommonContext } from "@/utils/context/common";
import useNavigation from "@/utils/useNavigate";
import { ProductModel } from "@akc/biz-api";
import React, { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const useRankAll = () => {
  // 一级、二级tabs数据
  const [level1Opts, setLevel1Opts] = React.useState<CategoryHotRankTabVO[]>([]);

  const { navigate2RankScale } = useNavigation();
  const { skin, track } = useCommonContext();
  const containerDom = useRef<HTMLDivElement>(null);

  const [selectedFirstIndex, setSelectedFirstIndex] = React.useState(0);
  // 当前选中的一级tab
  const activeLevel1Tab = level1Opts[selectedFirstIndex] || {};

  const [selectedSecondIndex, setSelectedSecondIndex] = React.useState(0);
  // 当前选中的二级tab
  const activeLevel2Tab = activeLevel1Tab?.tabItemVOList?.[selectedSecondIndex] || {};

  const [searchParam] = useSearchParams();


  // 当前tab下的榜单列表
  const [rankDataList, setRankDataList] = useState<HotRankDetailModel[]>();
  // 单榜单商品列表
  const [singleRankPrdList, setSingleRankPrdList] = useState<ProductModel[]>();
  const singleRankPageNumRef = useRef<number>(1);
  const [isSingleRank, setIsSingleRank] = useState(false); // 是否为单榜单模式

  const pageNum = useRef<number>(1);
  const [hasMore, setHasMore] = useState(true);
  const [showTabPop, setShowTabPop] = useState<boolean>(false);
  const commonContext = useCommonContext();
  const [loading, setLoading] = useState(false);
  const [showEmptyShowTip, setShowEmptyShowTip] = useState(false);
  const initEnd = useRef(false); // tab初始化完成标志

  // 获取分类数据，一二级一起返回
  const fetchCategoryData = async () => {
    try {
      const res = await hotRankCategoryList();
      if (res && res.length > 0) {
        setLevel1Opts(res);
      }
    } catch (error) {
      console.error("查询一级二级tabs数据异常", error);
    }
  };

  /**
   * 获取指定tab下的榜单数据
   * @param firstLevelIndex 一级tab索引
   * @param secondLevelIndex 二级tab索引
   */
  const getRankDataList = async (firstLevelIndex = 0, secondLevelIndex = 0) => {
    try {
      const pageNo = pageNum.current;
      const {
        categoryId,
        singleRankNo,
        singleRankName,
        categoryScene,
        tabItemVOList = [],
      } = level1Opts[firstLevelIndex];
      const _activeLevel2Tab = tabItemVOList?.[secondLevelIndex] || {};
      // 检查是否为4单榜单场景
      if (categoryScene === 4) {
        if (!isSingleRank) {
          setIsSingleRank(true);
        }
        if (!singleRankNo) {
          console.error("getRankDataLis停止执行，单榜单场景未返回榜单编号singleRankNo");
          return;
        }
        // 单榜单场景：调用商品列表接口
        await fetchSingleRankProducts(singleRankNo);
        return;
      }

      // 否则正常查榜单列表
      const queryParams = {
        rankNo: "", //当前榜单编码，查同级更多榜单=same_list时需要传入
        oneCategoryId: categoryId, // 一级类目ID
        twoCategoryId: _activeLevel2Tab?.tabCode, // 二级类目ID
        rankScene: "class_1", //使用场景： 查同级更多榜单=same_list，查询一二级类目下的榜单=class_1
        pageId: "H5_RankDetail",
        pageIndex: pageNo,
        pageSize: 10,
      };
      setLoading(true);
      const res = await getHotRankList(queryParams);
      setLoading(false);
      if (!res) {
        console.error("查询榜单列表当前页异常了");
        return;
      }
      setHasMore(res?.hasNext ?? false);

      // 如果第一页就没有榜单，则证明该tab下没有榜单，页面要提升报错
      if (pageNo === 1 && (res?.result?.length || 0) === 0) {
        setShowEmptyShowTip(true);
        return;
      }

      if (res?.result && res?.result.length > 0) {
        const flatResult = Array.isArray(res.result[0]) ? res.result.flat() : res.result;
        setRankDataList(prevData => {
          // 如果是第一页数据，直接替换而不是拼接，确保数据清空
          if (pageNo === 1) {
            return flatResult as HotRankDetailModel[];
          }
          return [...(prevData || []), ...(flatResult as HotRankDetailModel[])];
        });
      }
      if (res.pageIndex) {
        pageNum.current = res.pageIndex + 1;
      }
    } catch (error) {
      setLoading(false);
      setRankDataList([]);
      setHasMore(false);
      console.error("查询当前tab下的一页榜单列表异常了", error);
    }
  };

  // 获取一页单榜单商品数据
  const fetchSingleRankProducts = async (singleRankNo) => {
    if (!singleRankNo) {
      console.error("fetchSingleRankProducts停止执行，单榜单场景未返回榜单编号singleRankNo");
      return;
    }
    if (loading) {
      return;
    }
    try {
      setLoading(true);
      const res = await hotRankProductList({
        rankNo: singleRankNo,
        pageIndex: singleRankPageNumRef.current,
        pageSize: 10,
      });
      setLoading(false);
      setHasMore(res?.hasNext ?? false);
      if (Array.isArray(res?.result)) {
        setSingleRankPrdList(prevData => {
          // 如果是第一页数据，直接替换而不是拼接，确保数据清空
          if (singleRankPageNumRef.current === 1) {
            return res?.result as ProductModel[];
          }
          return [...(prevData || []), ...(res?.result as ProductModel[])];
        });
        singleRankPageNumRef.current++;
      }
    } catch (error) {
      setLoading(false);
      console.error("查询单榜单商品列表异常了", error);
    }
  };

  // 自动将可视区域滚动到指定的dom
  const scrollIntoTargetView = (domId: string) => {
    const tabElement = document.getElementById(domId);
    if (!tabElement) {
      console.error("scrollIntoTargetView失败，找不到dom元素", domId);
      return;
    }
    tabElement.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  };

  // 初始化页面一级二级tab数据
  React.useEffect(() => {
    pageNum.current = 1;
    fetchCategoryData();
  }, []);

  // 监听一级二级tab数据变化，执行tab初始化逻辑
  React.useEffect(() => {
    if (!level1Opts?.length) {
      return;
    }
    let levelOneId = searchParam.get("levelOneId") ?? "";//一级tabId
    const categoryOneId = searchParam.get("categoryOneId") || "";//二级tabId
    let initFirstIndex = 0;
    let initSecondIndex = 0;
    if(levelOneId){
      // 如果有传初始一级tabId，说明是新链接，可以直接定位到
      const _selectedFirstIndex = level1Opts.findIndex(item => item.categoryId === levelOneId);
      if(_selectedFirstIndex !== -1){
        initFirstIndex = _selectedFirstIndex;
        setSelectedFirstIndex(initFirstIndex);
        scrollIntoTargetView(`first-category-${initFirstIndex}`);


        if(categoryOneId){
          const tabItemVOList = level1Opts[initFirstIndex]?.tabItemVOList || [];
          const _selectedSecondIndex = tabItemVOList.findIndex(item => item.tabCode === categoryOneId);
          if(_selectedSecondIndex !== -1){
            initSecondIndex = _selectedSecondIndex;
            setSelectedSecondIndex(initSecondIndex);
            scrollIntoTargetView(`second-category-${initSecondIndex}`);
          }
        }
      }
    }else if (categoryOneId) {
      // 兼容历史url：只传了categoryOneId，默认定位到类目榜，查找categoryRankClassFlag=true
      const _selectedFirstIndex = level1Opts.findIndex(item => item.categoryRankClassFlag);
      if (_selectedFirstIndex !== -1) {
        initFirstIndex = _selectedFirstIndex;
        setSelectedFirstIndex(initFirstIndex);
        scrollIntoTargetView(`first-category-${initFirstIndex}`);
        // 查找tabCode=categoryOneId的二级tab
        const tabItemVOList = level1Opts[_selectedFirstIndex]?.tabItemVOList || [];
        const _selectedSecondIndex =tabItemVOList.findIndex(item => item.tabCode === categoryOneId);
        if (_selectedSecondIndex !== -1) {
            initSecondIndex = _selectedSecondIndex;
            setSelectedSecondIndex(initSecondIndex);
            scrollIntoTargetView(`second-category-${initSecondIndex}`);
        }
      }
    }
    // 初始请求
    getRankDataList(initFirstIndex, initSecondIndex).finally(() => {
      // 标记初始请求完成
      initEnd.current = true;
    });
  }, [level1Opts, searchParam]);

  // 监听tab索引变化，请求对应的榜单数据（仅用于用户手动切换tab及翻页时）
  React.useEffect(() => {
    if (level1Opts?.length && initEnd.current) {
      getRankDataList(selectedFirstIndex, selectedSecondIndex);
    }
  }, [level1Opts,selectedFirstIndex, selectedSecondIndex, pageNum,initEnd.current]);

  /**
   * 重置当前tab下的所有数据
   */
  const resetRankDataOfTab = () => {
    setShowTabPop(false);
    setRankDataList([]);
    setHasMore(false);
    setIsSingleRank(false);
    setSingleRankPrdList([]);
    setLoading(false);
    setShowEmptyShowTip(false);
    pageNum.current = 1;
    singleRankPageNumRef.current = 1;
  };
  /**
   * 切换一级tab
   * @param level1Index 切换后的一级tab索引
   */
  const onFirstTabClick = async (level1Index = 0) => {
    if(loading){
      console.warn("数据正在加载中，禁用切换操作");
      return;
    }
    if(level1Index === selectedFirstIndex){
      return;
    }
    if (track.track) {
      const categoryInfo = level1Opts[level1Index] || {};
      track.track("resource_click", {
        page_name: "榜单中心",
        previous_page_name: searchParam.get("page_name"),
        resource_type: "榜单分类",
        resource_name: categoryInfo?.categoryName,
      });
    }

    resetRankDataOfTab();
    setSelectedFirstIndex(level1Index);
    setSelectedSecondIndex(0); // 重置二级tab选择

    // 滚动到选中的tab
    const tabElement = document.getElementById(`first-category-${level1Index}`);
    if (tabElement) {
      tabElement.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  };

  /**
   * 切换二级tab
   * 注：不存在切换其他一级tab下二级tab的场景，所以这里不需要处理一级tab切换逻辑
   * @param level2Index  切换后的二级tab索引
   */
  const onSecondTabClick = (level2Index = 0) => {
    if(loading){
      console.warn("数据正在加载中，禁用切换操作");
      return;
    }
    if(level2Index === selectedSecondIndex){
      return;
    }
    if (track.track) {
      const categoryInfo = level1Opts[selectedFirstIndex] || {};
      const secondTab = categoryInfo?.tabItemVOList?.[level2Index] || {};
      track.track("resource_click", {
        page_name: "榜单中心",
        previous_page_name: searchParam.get("page_name"),
        resource_type: "榜单类型",
        resource_name: secondTab?.tabName || "全部",
      });
    }

    resetRankDataOfTab();
    setSelectedSecondIndex(level2Index);

    // 滚动到选中的tab
    const tabElement = document.getElementById(`second-category-${level2Index}`);
    if (tabElement) {
      tabElement.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  };

  const goToRankScale = (no?: string, index?: number) => {
    const query = {
      rankNo: no,
      fromAll: true,
    };
    navigate2RankScale(query);
  };

  const onShowTabPop = (isShow: boolean) => {
    setShowTabPop(isShow);
  };

  return {
    hasMore,
    selectedFirstIndex,
    activeLevel1Tab,
    selectedSecondIndex,
    activeLevel2Tab,
    onFirstTabClick,
    onSecondTabClick,
    onShowTabPop,
    containerDom,
    skin,
    level1Opts,
    showTabPop,
    rankDataList,
    singleRankPrdList,
    isSingleRank,
    getRankDataList,
    fetchSingleRankProducts,
    goToRankScale,
    commonContext,
    track,
    searchParam,
    loading,
    showEmptyShowTip,
    initEnd,
  };
};
